import { Info } from 'lucide-react';
import { ReactNode } from 'react';
import {
  Control,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

import { i18n } from '@cccom/shared/i18n';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../shadcn/form';
import { Input } from '../../shadcn/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../shadcn/tooltip';

export type CCFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<React.ComponentProps<typeof Input>, 'children'> & {
  control: Control<TFieldValues>;
  name: TName;
  label?: string;
  description?: string;
  inputProps?: React.ComponentProps<typeof Input> & { 'data-cy'?: string };
  children?:
    | ReactNode
    | ((
        field: ControllerRenderProps<TFieldValues, TName>,
        fieldState: ControllerFieldState
      ) => ReactNode);
  isRequired?: boolean;
  showOptionalHint?: boolean;
  tooltipLabel?: ReactNode;
  warningMessage?: string;
  'data-cy'?: string;
  formLabelProps?: string;
  showErrorMessage?: boolean;
};

const optionalLabel = (
  <span className="ml-2 text-gray-500">{i18n.t('forms.optional')}</span>
);

/**
 * Wrapper around shadcn form components to integrate with react-hook-form.
 * Note: This wrapper should always be used inside a <Form> component.
 *
 * @see https://ui.shadcn.com/docs/components/form
 */

export function CCFormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  children,
  isRequired = false,
  showOptionalHint = false,
  tooltipLabel,
  warningMessage,
  description,
  inputProps,
  formLabelProps,
  showErrorMessage = true,
  ...rest
}: CCFormFieldProps<TFieldValues, TName>) {
  const renderFormControl = (
    field: ControllerRenderProps<TFieldValues, TName>,
    fieldState: ControllerFieldState
  ) => {
    if (!children) {
      return (
        <Input
          {...field}
          {...rest}
          {...inputProps}
          className="bg-white"
          data-cy={rest['data-cy']}
          aria-label={`input-${label}`}
          autoComplete={
            inputProps?.type === 'password' ? 'new-password' : 'off'
          }
          onChange={(e) => {
            field.onChange(e);
            inputProps?.onChange?.(e);
          }}
          value={field.value ?? ''}
        />
      );
    }

    if (typeof children === 'function') {
      return children(field, fieldState);
    }

    return children;
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className="w-full">
          {label && (
            <div className="flex items-center gap-1">
              <FormLabel
                className="text-sm font-medium text-gray-900"
                {...(formLabelProps ? { htmlFor: formLabelProps } : {})}
              >
                {label}
                {isRequired && <span className="ml-1">*</span>}
                {showOptionalHint ? optionalLabel : null}
              </FormLabel>
              {tooltipLabel && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="text-muted-foreground ml-1 h-4 w-4 cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[220px] text-xs">
                    {tooltipLabel}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )}

          <FormControl>{renderFormControl(field, fieldState)}</FormControl>

          {description && <FormDescription>{description}</FormDescription>}

          {warningMessage && (
            <p className="mt-1 text-sm text-red-500">{warningMessage}</p>
          )}

          {showErrorMessage && <FormMessage className="font-normal" />}
        </FormItem>
      )}
    />
  );
}
